import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, hlaskaZChyby } from '../../api/client.js';
import type { DenikPamet, HeadcountZaznam } from '../../api/types.js';
import { Input } from '../../components/ui.js';
import { odesliNeboZarad } from '../../lib/offline.js';

/**
 * Headcount subek (BOZP): „kolik lidí od subky X dnes" — anonymní počet,
 * NENÍ docházka (žádná identita, nejde do mezd). Opakovaný zápis téhož dne
 * počet aktualizuje (upsert na API). Offline jde do fronty.
 */

function dnesIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
}

export function HeadcountKarta({
  zakazkaId,
  pamet,
  naChybu,
}: {
  zakazkaId: string;
  pamet?: DenikPamet;
  naChybu: (ch: string | null) => void;
}) {
  const queryClient = useQueryClient();
  const datum = dnesIso();
  const [pocty, setPocty] = useState<Record<string, number>>({});

  const { data: dnesni } = useQuery({
    queryKey: ['headcount', zakazkaId, datum],
    queryFn: () =>
      api.get<HeadcountZaznam[]>(
        `/api/headcount?zakazkaId=${encodeURIComponent(zakazkaId)}&datum=${datum}`,
      ),
  });

  // Subky = dnešní záznamy + firmy s vazbou na subdodavatele z paměti deníku.
  const subky = new Map<string, string>();
  for (const z of dnesni ?? []) subky.set(z.subdodavatel.id, z.subdodavatel.nazev);
  for (const f of pamet?.firmy ?? []) {
    if (f.subdodavatelId && !subky.has(f.subdodavatelId)) subky.set(f.subdodavatelId, f.nazev);
  }

  const pocet = (subId: string): number =>
    pocty[subId] ?? dnesni?.find((z) => z.subdodavatel.id === subId)?.pocetOsob ?? 0;

  const zapis = async (subdodavatelId: string, pocetOsob: number) => {
    if (pocetOsob < 0) return;
    setPocty((p) => ({ ...p, [subdodavatelId]: pocetOsob }));
    naChybu(null);
    try {
      const hned = await odesliNeboZarad({
        druh: 'json',
        metoda: 'POST',
        url: '/api/headcount',
        telo: { zakazkaId, datum, subdodavatelId, pocetOsob },
        popis: `Headcount ${datum}`,
      });
      if (hned) {
        void queryClient.invalidateQueries({ queryKey: ['headcount', zakazkaId, datum] });
      }
    } catch (err) {
      naChybu(hlaskaZChyby(err));
    }
  };

  if (subky.size === 0) return null;

  return (
    <section className="rounded-xl border border-border bg-surface p-4">
      <h2 className="text-lg font-bold">Lidé od subek dnes (BOZP)</h2>
      <p className="text-sm text-text-secondary">
        Evidence pro BOZP — kolik lidí od subdodavatelů dnes bylo na stavbě. Jen počet u brány
        (žádná jména, žádné mzdy). Nezamyká se podpisem dne.
      </p>
      <ul className="mt-1 flex flex-col">
        {[...subky.entries()].map(([subId, nazev]) => (
          <li
            key={subId}
            className="flex min-h-12 items-center justify-between gap-2 border-t border-border first:border-t-0"
          >
            <span className="min-w-0 truncate text-base font-semibold">{nazev}</span>
            <span className="flex items-center gap-1">
              <button
                type="button"
                aria-label={`Ubrat — ${nazev}`}
                className="min-h-12 min-w-12 rounded-lg border-2 border-border-strong text-xl font-bold"
                onClick={() => void zapis(subId, pocet(subId) - 1)}
              >
                −
              </button>
              <Input
                aria-label={`Počet osob — ${nazev}`}
                type="number"
                min={0}
                className="!w-16 text-center"
                value={pocet(subId)}
                onChange={(e) => void zapis(subId, Number(e.target.value))}
              />
              <button
                type="button"
                aria-label={`Přidat — ${nazev}`}
                className="min-h-12 min-w-12 rounded-lg border-2 border-border-strong text-xl font-bold"
                onClick={() => void zapis(subId, pocet(subId) + 1)}
              >
                +
              </button>
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
